import { useState } from "react";
import axios from "axios";
import { CalendarClock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { CALENDLY_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  "Student Assembly",
  "Professional Development",
  "Parent Engagement Event",
  "District Partnership",
  "Keynote / Conference",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    event_date: "",
    inquiry_type: "Student Assembly",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      toast.success("Inquiry sent — CDaught's team will be in touch shortly.");
      setForm({
        name: "",
        organization: "",
        email: "",
        phone: "",
        event_date: "",
        inquiry_type: "Student Assembly",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please email info@cdaughtspeaks.com directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-navy text-white py-20 md:py-28 overflow-hidden grain">
      <Toaster position="top-center" richColors />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsla(46,65%,52%,0.15),transparent_60%)]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Left intro */}
        <div className="lg:col-span-5">
          <div className="eyebrow text-gold">Booking & Partnership</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-white mt-3 font-bold tracking-tight leading-[1.05]">
            Bring CDaught to your
            <span className="block italic font-medium text-gold">school or organization.</span>
          </h2>
          <p className="mt-6 text-white/75 text-[15px] leading-relaxed max-w-md">
            Whether you need a keynote speaker, PD facilitator, student assembly presenter, or a
            district partner, we&apos;re ready to cultivate an environment conducive to growth with you.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-discovery-btn"
              className="flex items-center justify-between bg-white/5 border border-white/15 hover:border-gold p-4 group transition-colors"
            >
              <div className="flex items-center gap-3">
                <CalendarClock size={20} className="text-gold" />
                <div>
                  <div className="text-[14px] font-semibold">Schedule a Discovery Call</div>
                  <div className="text-[12px] text-white/55">15-min intro on Calendly</div>
                </div>
              </div>
              <span className="text-gold opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </a>
            <a
              href="mailto:info@cdaughtspeaks.com"
              data-testid="contact-email-link"
              className="flex items-center justify-between bg-white/5 border border-white/15 hover:border-gold p-4 group transition-colors"
            >
              <div>
                <div className="text-[12px] tracking-[0.18em] uppercase text-white/55">Email</div>
                <div className="text-[14px] font-semibold mt-0.5">info@cdaughtspeaks.com</div>
              </div>
              <span className="text-gold opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </a>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white text-navy p-7 md:p-10"
        >
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 size={48} className="text-gold mx-auto" strokeWidth={1.4} />
              <h3 className="font-serif text-3xl text-navy mt-4 font-bold">Thank you.</h3>
              <p className="mt-2 text-navy/65 text-[15px] max-w-sm mx-auto">
                Your message is in. We&apos;ll follow up within 1–2 business days.
              </p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-reset"
                className="mt-6 text-sm font-semibold text-navy underline underline-offset-4 hover:text-gold"
              >
                Send another inquiry
              </button>
            </div>
          ) : (
            <>
              <div className="eyebrow text-gold">Inquiry Form</div>
              <h3 className="font-serif text-3xl text-navy mt-2 font-bold">Tell us about your event.</h3>

              <div className="mt-7 grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" testid="field-name">
                  <input
                    value={form.name}
                    onChange={update("name")}
                    required
                    data-testid="input-name"
                    className="input"
                  />
                </Field>
                <Field label="Organization" testid="field-org">
                  <input
                    value={form.organization}
                    onChange={update("organization")}
                    data-testid="input-organization"
                    className="input"
                  />
                </Field>
                <Field label="Email *" testid="field-email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    data-testid="input-email"
                    className="input"
                  />
                </Field>
                <Field label="Phone" testid="field-phone">
                  <input
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="input-phone"
                    className="input"
                  />
                </Field>
                <Field label="Event Date" testid="field-date">
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={update("event_date")}
                    data-testid="input-event-date"
                    className="input"
                  />
                </Field>
                <Field label="Inquiry Type" testid="field-type">
                  <select
                    value={form.inquiry_type}
                    onChange={update("inquiry_type")}
                    data-testid="input-inquiry-type"
                    className="input"
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Message *" testid="field-msg" className="mt-4">
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={5}
                  placeholder="Audience, goals, dates, anything we should know…"
                  data-testid="input-message"
                  className="input resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-gold mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm font-semibold text-[14px] disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? "Sending…" : "Send Inquiry"}
              </button>
            </>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, children, testid, className = "" }) {
  return (
    <label data-testid={testid} className={`block ${className}`}>
      <span className="text-[11px] tracking-[0.18em] uppercase text-navy/55 font-semibold">{label}</span>
      <div className="mt-1.5">{children}</div>
      <style>{`
        .input {
          width: 100%;
          background: #fff;
          border: 1px solid hsl(var(--line));
          padding: 0.7rem 0.85rem;
          font-size: 14px;
          color: hsl(var(--navy));
          outline: none;
          border-radius: 2px;
          transition: border-color .2s, box-shadow .2s;
        }
        .input:focus {
          border-color: hsl(var(--navy));
          box-shadow: 0 0 0 3px hsla(217,66%,11%,0.08);
        }
      `}</style>
    </label>
  );
}
